// Constants
import { Key } from "@ms/uno-constants/lib/local/KeyCode";
import { ElementIds } from "@ms/uno-constants/lib/local/UIConstants";
// Controls
import { Button, DrawerHeader, DrawerHeaderTitle, Field, Input, OverlayDrawer, Spinner, Tab, TabList, Tooltip } from "@fluentui/react-components";
import { ColorFilled, ColorRegular, SettingsFilled, SettingsRegular } from "@fluentui/react-icons";
import { ConfirmationDialog } from "@ms/planner-generic-controls/lib/local/confirmationDialog/ConfirmationDialog";
import { Dismiss } from "@ms/planner-generic-controls/lib/local/icons";
import { ContainerSettingsForm } from "../containerSettingsForm/ContainerSettingsForm";
import { PlanSettings } from "../planSettings/PlanSettings";
import { Plan } from "@ms/planner-models/lib/local/client/planner/plan/Plan";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { usePlanSettingsPanelStyles, usePlanSettingsTabListStyles } from "./PlanSettingsPanel.styles";
// Utilities
import isEmpty from "lodash/isEmpty";
import trim from "lodash/trim";
import * as React from "react";
var SettingsPivots = /*#__PURE__*/ function(SettingsPivots) {
    /** Suggested backgrounds pivot for plan background selection */ SettingsPivots["SuggestedBackgrounds"] = "SuggestedBackgrounds";
    /** General settings pivot for plan configuration */ SettingsPivots["Settings"] = "Settings";
    return SettingsPivots;
}(SettingsPivots || {});
export const PlanSettingsPanel = ({ plan, modernGroup, isOpen, canDeletePlan, canRenamePlan, canUpdateVisuals, isTabView, isGroupOwner, canChangeSensitivity, onUpdatePlanTitle, onDeletePlan, onDismiss, elementToFocusOnDismiss, targetElementId, onUpdatePlanBackground, fetchImageSuggestions, backgroundSuggestions, areBackgroundSuggestionsEnabled = true, sensitivityLabels, currentSensitivityLabelId, onUpdateGroupSettings, isUpdatingGroupSettings, hasSharedContainers, enableMIPLabels, userAccessibleTenantSensitivityLabels, plannerRoster, onSensitivitySelected })=>{
    const styles = usePlanSettingsPanelStyles();
    const tabListStyles = usePlanSettingsTabListStyles();
    // Settings tab visibility logic: show for Group and Roster containers.
    // - Group containers: show unconditionally; GroupSettingsForm handles its own loading/spinner.
    // - Roster containers: gate on !!plannerRoster to avoid a blank panel while data loads,
    //   since ContainerSettingsForm returns null (no spinner) when plannerRoster is absent.
    // - Other container types: no settings tab.
    const shouldShowSettingsTab = React.useMemo(()=>{
        const containerType = plan.container.containerType;
        if (containerType === ContainerType.Group) {
            return true;
        }
        if (containerType === ContainerType.Roster) {
            return !!plannerRoster;
        }
        return false;
    }, [
        plan.container.containerType,
        plannerRoster
    ]);
    const [selectedPivot, setSelectedPivot] = React.useState("SuggestedBackgrounds");
    const [planTitle, setPlanTitle] = React.useState(plan.displayName || "");
    const titleInputRef = React.useRef(null);
    // State to track container settings changes (updated by ContainerSettingsForm)
    const [containerSettingsSaveHandler, setContainerSettingsSaveHandler] = React.useState(null);
    const [hasContainerSettingsChanged, setHasContainerSettingsChanged] = React.useState(false);
    const [isSaveContainerSettingsDisabled, setIsSaveContainerSettingsDisabled] = React.useState(false);
    // Stable callbacks for container settings state changes - wrapped in useCallback to prevent render loops
    const handleSaveHandlerChange = React.useCallback((handler)=>{
        setContainerSettingsSaveHandler(()=>handler);
    }, []);
    const handleHasChangesChange = React.useCallback((hasChanges)=>{
        setHasContainerSettingsChanged(hasChanges);
    }, []);
    const handleSaveDisabledChange = React.useCallback((isDisabled)=>{
        setIsSaveContainerSettingsDisabled(isDisabled);
    }, []);
    // Validation state
    const [isPlanTitleEmpty, setIsPlanTitleEmpty] = React.useState(false);
    // Unsaved changes dialog state
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = React.useState(false);
    // Determine if this specific plan can be deleted (tab view does not support delete)
    const canDeleteThisPlan = canDeletePlan && !isTabView;
    // Group non-owner who can't delete: keep the button focusable so the owner-only Tooltip can explain why.
    // Uses raw canDeletePlan (not canDeleteThisPlan) so a tab-view-only disable stays plainly disabled.
    const cannotDeleteThisGroupPlanNotOwner = plan.container.containerType === ContainerType.Group && !canDeletePlan && isGroupOwner === false;
    React.useEffect(()=>{
        setPlanTitle(plan.displayName || "");
    }, [
        plan.displayName
    ]);
    // Reset form fields to original values when panel opens.
    // This ensures discarded changes don't persist and users always start with current values.
    React.useEffect(()=>{
        if (isOpen) {
            setPlanTitle(plan.displayName);
            setIsPlanTitleEmpty(false);
            titleInputRef.current?.focus();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOpen
    ]);
    // Keep the selected pivot valid as tab visibility changes.
    // SuggestedBackgrounds is the default content host: even when its tab is hidden
    // (e.g. in Gov, where areBackgroundSuggestionsEnabled is false), its pane still
    // renders the plan title and the delete button, so it is always a safe fallback.
    // The Settings pane, by contrast, only exists when the Settings tab is available.
    React.useEffect(()=>{
        const isSettingsSelectedButHidden = selectedPivot === "Settings" && !shouldShowSettingsTab;
        const isBackgroundsSelectedButHidden = selectedPivot === "SuggestedBackgrounds" && !areBackgroundSuggestionsEnabled;
        if (isSettingsSelectedButHidden) {
            // Fall back to SuggestedBackgrounds, which always has renderable content.
            setSelectedPivot("SuggestedBackgrounds");
        } else if (isBackgroundsSelectedButHidden && shouldShowSettingsTab) {
            // Backgrounds tab is hidden but Settings is available, so prefer Settings.
            setSelectedPivot("Settings");
        }
    }, [
        shouldShowSettingsTab,
        selectedPivot,
        areBackgroundSuggestionsEnabled
    ]);
    // Validation helper functions
    const getPlanTitleError = ()=>{
        if (isPlanTitleEmpty) {
            return Strings.PlanSettings_NameError_Empty;
        }
        return "";
    };
    const handlePlanTitleChange = React.useCallback((event)=>{
        const value = event.target.value;
        setPlanTitle(value);
        setIsPlanTitleEmpty(isEmpty(trim(value)));
    }, []);
    const handlePlanTitleBlur = ()=>{
        if (isPlanTitleEmpty) {
            // Reset to original if user entered only whitespace/empty
            setPlanTitle(plan.displayName || "");
            return;
        }
        // Trim and compare with original to avoid unnecessary updates
        if (planTitle.trim() !== plan.displayName) {
            onUpdatePlanTitle(planTitle.trim());
        } else {
            // Reset to original if no actual change
            setPlanTitle(plan.displayName || "");
        }
    };
    const handlePlanTitleKeyDown = (event)=>{
        if (event.key === Key.ENTER) {
            event.currentTarget.blur();
        } else if (event.key === Key.ESCAPE) {
            setPlanTitle(plan.displayName || "");
            event.currentTarget.blur();
        }
    };
    const dismissAndRestoreFocus = React.useCallback(()=>{
        onDismiss();
        requestAnimationFrame(()=>elementToFocusOnDismiss?.focus());
    }, [
        onDismiss,
        elementToFocusOnDismiss
    ]);
    const handleRequestDismiss = React.useCallback(()=>{
        // Only show warning if there are valid unsaved container settings changes
        // Don't prompt if save is disabled (validation error) - those aren't saveable changes
        if (hasContainerSettingsChanged && !isSaveContainerSettingsDisabled) {
            setShowUnsavedChangesDialog(true);
        } else {
            dismissAndRestoreFocus();
        }
    }, [
        hasContainerSettingsChanged,
        isSaveContainerSettingsDisabled,
        dismissAndRestoreFocus
    ]);
    const handleOpenChange = (event, data)=>{
        if (!data.open) {
            handleRequestDismiss();
        }
    };
    const handleKeyDown = (event)=>{
        if (event.key === Key.ESCAPE) {
            handleRequestDismiss();
            event.preventDefault();
            event.stopPropagation();
        }
    };
    const renderSettingsContent = ()=>{
        const settingsContentDiv = /*#__PURE__*/ React.createElement("div", {
            className: styles.settingsContentContainer
        }, /*#__PURE__*/ React.createElement(ContainerSettingsForm, {
            plan: plan,
            modernGroup: modernGroup || null,
            isGroupOwner: isGroupOwner ?? false,
            sensitivityLabels: sensitivityLabels || null,
            onUpdateGroupSettings: onUpdateGroupSettings,
            isUpdatingGroupSettings: isUpdatingGroupSettings,
            plannerRoster: plannerRoster,
            hasSharedContainers: hasSharedContainers,
            enableMIPLabels: enableMIPLabels,
            userAccessibleTenantSensitivityLabels: userAccessibleTenantSensitivityLabels,
            canChangeSensitivity: canChangeSensitivity,
            currentSensitivityLabelId: currentSensitivityLabelId,
            onSaveHandlerChange: handleSaveHandlerChange,
            onHasChangesChange: handleHasChangesChange,
            onSaveDisabledChange: handleSaveDisabledChange,
            onSensitivitySelected: onSensitivitySelected
        }));
        const deletePlanButton = /*#__PURE__*/ React.createElement(Button, {
            "data-testid": "plan-settings-panel-button-delete",
            appearance: "outline",
            onClick: ()=>onDeletePlan(plan.id),
            className: styles.deleteButton,
            disabled: !canDeleteThisPlan && !cannotDeleteThisGroupPlanNotOwner,
            disabledFocusable: cannotDeleteThisGroupPlanNotOwner
        }, Strings.PlanSettings_DeletePlan);
        const actionButtonsFooter = /*#__PURE__*/ React.createElement("div", {
            className: styles.footer
        }, containerSettingsSaveHandler && /*#__PURE__*/ React.createElement(Button, {
            "data-testid": "plan-settings-panel-button-save",
            appearance: "primary",
            type: "button",
            onClick: ()=>{
                containerSettingsSaveHandler();
            },
            disabled: !hasContainerSettingsChanged || isSaveContainerSettingsDisabled || isUpdatingGroupSettings,
            "aria-label": isUpdatingGroupSettings ? Strings.PlanSettings_SavingGroupSettingsAriaLabel : Strings.PlanSettings_SaveGroupSettingsAriaLabel,
            "aria-busy": isUpdatingGroupSettings,
            icon: isUpdatingGroupSettings ? /*#__PURE__*/ React.createElement(Spinner, {
                size: "tiny"
            }) : undefined,
            className: styles.saveButton
        }, isUpdatingGroupSettings ? Strings.PlanSettings_SavingGroupSettingsButton : Strings.SharedComponents_SaveChangesButtonText), cannotDeleteThisGroupPlanNotOwner ? /*#__PURE__*/ React.createElement(Tooltip, {
            content: Strings.PlanSettings_CannotDeletePlan,
            relationship: "description",
            positioning: "above",
            withArrow: true
        }, deletePlanButton) : deletePlanButton);
        return /*#__PURE__*/ React.createElement("div", {
            className: styles.settingsContainerWrapper
        }, settingsContentDiv, actionButtonsFooter);
    };
    const renderPlanSettingsContent = ()=>{
        if (!plan?.id) {
            return /*#__PURE__*/ React.createElement("div", {
                role: "status",
                "aria-live": "polite"
            }, /*#__PURE__*/ React.createElement(Spinner, {
                size: "small"
            }), Strings.TaskAssignments_LoadingText);
        }
        if (selectedPivot === "SuggestedBackgrounds") {
            return /*#__PURE__*/ React.createElement(PlanSettings, {
                plan: plan,
                canDeletePlan: canDeleteThisPlan,
                isGroupOwner: isGroupOwner,
                isTabView: isTabView,
                currentBackgroundUrl: plan.planView?.visuals?.background?.overlayImage ?? null,
                onUpdatePlanTitle: onUpdatePlanTitle,
                onDeletePlan: onDeletePlan,
                onUpdatePlanBackground: onUpdatePlanBackground,
                fetchImageSuggestions: fetchImageSuggestions,
                backgroundSuggestions: backgroundSuggestions || null,
                canUpdateVisuals: canUpdateVisuals,
                areBackgroundSuggestionsEnabled: areBackgroundSuggestionsEnabled
            });
        }
        if (selectedPivot === "Settings") {
            return renderSettingsContent();
        }
        return null;
    };
    return /*#__PURE__*/ React.createElement(OverlayDrawer, {
        open: isOpen,
        onOpenChange: handleOpenChange,
        position: "end",
        size: "medium",
        modalType: "non-modal",
        "aria-labelledby": ElementIds.PlanSettingsTitle,
        "aria-describedby": `${ElementIds.PlanSettingsContent} ${"plan-settings-description"}`,
        onKeyDown: handleKeyDown,
        "data-testid": "plan-settings-panel"
    }, /*#__PURE__*/ React.createElement(DrawerHeader, {
        className: styles.drawerHeader
    }, /*#__PURE__*/ React.createElement(DrawerHeaderTitle, {
        id: ElementIds.PlanSettingsTitle,
        tabIndex: -1,
        action: /*#__PURE__*/ React.createElement(Button, {
            "data-testid": "plan-settings-panel-button-dismiss",
            appearance: "subtle",
            "aria-label": Strings.SharedComponents_CloseButtonText,
            icon: /*#__PURE__*/ React.createElement(Dismiss, null),
            onClick: handleRequestDismiss
        })
    })), /*#__PURE__*/ React.createElement("div", {
        id: ElementIds.PlanSettingsContent,
        className: styles.content,
        role: "group",
        "aria-label": Strings.PlanSettingsPanel_PlanSettingsPanelTitle
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.planTitleContainer
    }, /*#__PURE__*/ React.createElement(Field, {
        validationMessage: getPlanTitleError(),
        validationState: isPlanTitleEmpty ? "error" : "none"
    }, /*#__PURE__*/ React.createElement(Input, {
        "data-testid": "plan-settings-panel-input",
        ref: titleInputRef,
        value: planTitle,
        onChange: handlePlanTitleChange,
        onBlur: handlePlanTitleBlur,
        onKeyDown: handlePlanTitleKeyDown,
        maxLength: Plan.NameMaxLength,
        className: styles.planTitleInput,
        placeholder: Strings.PlanSettings_PlanNamePlaceholder,
        "aria-label": Strings.PlanSettings_PlanNamePlaceholder,
        disabled: !canRenamePlan
    }))), (areBackgroundSuggestionsEnabled || shouldShowSettingsTab) && /*#__PURE__*/ React.createElement("div", {
        className: styles.pivots
    }, /*#__PURE__*/ React.createElement(TabList, {
        "data-testid": "plan-settings-panel-tab-list",
        className: tabListStyles.root,
        selectedValue: selectedPivot,
        onTabSelect: (event, data)=>setSelectedPivot(data.value),
        appearance: "subtle",
        size: "medium"
    }, areBackgroundSuggestionsEnabled && /*#__PURE__*/ React.createElement(Tab, {
        "data-testid": "plan-settings-panel-tab-suggested-backgrounds",
        className: tabListStyles.tab,
        value: "SuggestedBackgrounds",
        icon: selectedPivot === "SuggestedBackgrounds" ? /*#__PURE__*/ React.createElement(ColorFilled, null) : /*#__PURE__*/ React.createElement(ColorRegular, null)
    }, Strings.PlanSettings_SuggestedBackgroundsLabel), shouldShowSettingsTab && /*#__PURE__*/ React.createElement(Tab, {
        "data-testid": "plan-settings-panel-tab-settings",
        className: tabListStyles.tab,
        value: "Settings",
        icon: selectedPivot === "Settings" ? /*#__PURE__*/ React.createElement(SettingsFilled, null) : /*#__PURE__*/ React.createElement(SettingsRegular, null)
    }, Strings.PlanSettingsPanel_SettingsPivotTitle))), /*#__PURE__*/ React.createElement("div", {
        className: styles.settingsContent
    }, renderPlanSettingsContent())), showUnsavedChangesDialog && /*#__PURE__*/ React.createElement(ConfirmationDialog, {
        title: Strings.PlanSettings_UnsavedChangesTitle,
        message: Strings.PlanSettings_UnsavedChangesMessage,
        primaryButtonText: Strings.PlanSettings_DiscardChangesButton,
        secondaryButtonText: Strings.SharedComponents_CancelButtonText,
        onPrimaryAction: ()=>{
            setShowUnsavedChangesDialog(false);
            dismissAndRestoreFocus();
        },
        onSecondaryAction: ()=>setShowUnsavedChangesDialog(false),
        onDismiss: ()=>setShowUnsavedChangesDialog(false)
    }));
};
